import { merge } from 'lodash';
import React, { ComponentType } from 'react';
import { css, DefaultTheme, ThemeProvider } from 'styled-components';
import { confirmingStyle } from './confirmTwice';
import { ZTooltipLookContext } from '../../../../widgets/ztooltip';
import { DropdownToneContext } from '../../../../widgets/dropdown';
import { OutlineButton } from '../../../../widgets/buttons';
import { CompactInput } from '../../../../widgets/input';
import { accessibleAccent, AccessibleAccent } from 'src/shared/theme';

// I COLORI DEI DUE EDITOR, IN UN POSTO SOLO (prototipo V3, AGGIORNAMENTI §1; utente, 2026-10-19).
//
// Scene Editor e Composer sono due ambienti della stessa piattaforma e leggono la stessa gerarchia:
// cornice < fondo dell'Inspector < pannello = card, con ciò che galleggia (menu, modali) sul bianco.
// I valori sono quelli chiari del prototipo (Neutral v2); il testo «faint» e il «chapter» sono un
// passo più scuri, per reggere 4.5:1 e 3:1 sul fondo dell'Inspector.
//
// SOLO DENTRO GLI EDITOR: il tema del Backoffice resta com'è per tutte le altre pagine. Qui si
// sovrascrivono i suoi grigi (`editorTheme`), così i componenti che li leggono dal tema — il pannello
// delle proprietà, i pannelli del workspace, il rail — prendono questi senza saperlo.
//
// TRE ARANCI, UN COMPITO CIASCUNO (utente, 2026-10-22, accessibilità; `accessibleAccent`):
//   brand  #E8650A  le superfici: lavati, fili, il velo della ricerca (`--editor-accent-brand`)
//   fill   #C94F00  i pieni con testo bianco: Save, il Primary delle finestre, interruttori e barre accese (`--editor-accent-fill`)
//   ink    #A94700  tutto ciò che è disegnato in arancio: testo, icone, anelli, il fuoco; e il passaggio del pieno
//                   (`--editor-accent`, e `theme.app.secondary` dentro gli editor)
// L'arancio del marchio su bianco ha 3,3:1: non basta né al testo (4,5:1) né, sui fondi grigi, ai segni (3:1).
// Un marketplace col suo marchio ha i suoi tre toni, ricavati dal suo colore.
// CINQUE GRIGI, CON GRADINI CHE SI VEDONO (utente, 2026-10-23). Le superfici erano otto, un gradino ogni due punti di
// luminosità: differenze di 0,7–1,7 ΔE2000, cioè sotto o appena sopra quello che l'occhio distingue. Ora fra un grigio
// e l'altro ce ne sono 2,5–3. Assorbiti: raised (#FFFFFF) nel pannello, che diventa bianco; hover (#E3E5E8) e lineSoft
// (#E0E2E5) nell'incavo.
//   panel   #FFFFFF  pannelli, card, menu, segmento scelto
//   field   #F1F2F4  campi, passaggio sulle righe
//   sunken  #E9EBED  fondo dell'Inspector, passaggio sui bottoni, pista delle pillole, divisori interni
//   chrome  #DBDDE1  cornice e testata
//   line    #D1D3D8  bordi di card, slot, divisori
//   fieldLine #949499  il filo dei CAMPI modificabili (numeri, testo, colore, tendine, ricerca): 3,0:1 sul bianco, come
//                   chiede WCAG 1.4.11 per riconoscere un controllo (utente, 2026-10-23). Card e divisori restano tenui.
// Testo: fg, muted (secondario), faint (tenue: etichette di sezione, didascalie). Il testo tenue NON va sulla cornice
// (chrome): lì ha 4,0:1, sotto il 4,5:1 del testo piccolo.
//
// LA SCALA DEI CARATTERI È DI CINQUE MISURE (utente, 2026-10-23; erano diciassette, con le mezze misure):
//   15 / 500      titoli di pagine e finestre
//   13 / 400–600  righe, nomi, testata
//   12 / 400      campi, etichette, bottoni piccoli
//   11 / 400–500  didascalie, metadati, conteggi, barra di stato: il minimo per il testo normale
//   10 / 600      solo in maiuscolo con spaziatura: etichette di sezione e di tipo
// Pesi 400, 500 e 600: il 700 non c'è.
export const EDITOR_COLORS = {
    chrome: '#dbdde1',
    sunken: '#e9ebed',
    panel: '#ffffff',
    field: '#f1f2f4',
    line: '#d1d3d8',
    fieldLine: '#949499',
    fg: '#262a30',
    muted: '#55595f',
    faint: '#676a6e'
};

/** I tre toni per chi sta dentro gli editor; fuori (una finestra col tema del Backoffice) si ricavano dal marchio. */
export const editorAccent = (theme?: DefaultTheme): AccessibleAccent => theme?.app?.editorAccent ?? accessibleAccent(theme?.app?.secondary);

/**
 * Il tema del Backoffice con i grigi degli editor al posto dei suoi, e I BOTTONI CONDIVISI NELLE RICETTE DEGLI EDITOR
 * (utente, 2026-10-20, `docs/mappatura-bottoni-editor.md`): il Primary diventa l'azione principale tenue (lavato, filo e
 * parola in arancio), il Basic diventa il secondario neutro (filo grigio, niente arancio), i bottoni testo l'arancio a
 * peso medio. Dentro gli editor `secondary` è l'inchiostro (`editorAccent(theme).ink`): ciò che lo legge per disegnare
 * — contorni, anelli, icone — è già accessibile; checkbox e interruttori accesi prendono il pieno.
 */
// IL PRIMARIO PIENO DEGLI EDITOR (utente, 2026-10-22): Save in testata, il ritorno all'editor in Test mode e l'azione
// delle finestre di domanda. Il pieno accessibile, al passaggio l'inchiostro: testo bianco a 4,6:1 e 5,9:1.
const solidPrimary = (background: string) => ({ backgroundColor: background, textColor: '#FFFFFF' });
const editorPrimaryButton = ({ fill, ink }: AccessibleAccent) => ({
    border: `1px solid ${fill}`,
    default: solidPrimary(fill),
    hover: solidPrimary(ink),
    active: solidPrimary(ink),
    disabled: { ...solidPrimary(fill), border: `1px solid ${fill}` }
});

// Il tema delle finestre di domanda: i bottoni di Zakeke (Primary e Basic), col Primary nel pieno degli editor.
export const questionDialogTheme = (theme?: DefaultTheme): DefaultTheme =>
    merge({}, theme, { app: { component: { button: { primary: editorPrimaryButton(editorAccent(theme)) } } } });

export const editorTheme = (theme?: DefaultTheme): DefaultTheme => {
    const accent = editorAccent(theme);
    const { brand, fill, ink } = accent;
    const wash = `color-mix(in srgb, ${brand} 8%, #ffffff)`;
    const washHover = `color-mix(in srgb, ${brand} 14%, #ffffff)`;
    const accentLine = `1px solid color-mix(in srgb, ${brand} 38%, ${EDITOR_COLORS.sunken})`;
    const neutralLine = `1px solid ${EDITOR_COLORS.line}`;
    return merge({}, theme, {
        app: {
            pageBackgroundColor: EDITOR_COLORS.sunken,
            backgroundToolbar: EDITOR_COLORS.field,
            borderColor: EDITOR_COLORS.line,
            textColor: EDITOR_COLORS.fg,
            primary: EDITOR_COLORS.muted,
            secondary: ink,
            editorAccent: accent,
            component: {
                icon: { help: { color: ink } },
                checkbox: { selected: { active: { background: fill, border: fill, inputColor: fill } } },
                switch: { selected: { backgroundColor: fill } },
                radio: { selected: { active: { border: `1px solid ${ink}`, inputColor: ink } } },
                pageBlocksContainer: { pageBlock: { backgroundColor: EDITOR_COLORS.panel } },
                button: {
                    primary: {
                        border: accentLine,
                        default: { backgroundColor: wash, textColor: ink },
                        hover: { backgroundColor: washHover, textColor: ink },
                        active: { backgroundColor: washHover, textColor: ink },
                        disabled: { backgroundColor: wash, textColor: ink, border: accentLine }
                    },
                    basic: {
                        border: neutralLine,
                        default: { backgroundColor: EDITOR_COLORS.panel, textColor: EDITOR_COLORS.fg },
                        hover: { backgroundColor: EDITOR_COLORS.sunken, textColor: EDITOR_COLORS.fg },
                        active: { backgroundColor: EDITOR_COLORS.sunken, textColor: EDITOR_COLORS.fg },
                        disabled: { backgroundColor: EDITOR_COLORS.panel, textColor: EDITOR_COLORS.fg, border: neutralLine }
                    }
                },
                textButton: {
                    fontWeight: 500,
                    primary: { default: { color: ink }, hover: { color: ink }, active: { color: ink } }
                }
            }
        }
    });
};

// Gli editor accendono anche il loro tooltip: scuro e senza freccia, come nel prototipo (ZTooltipLookContext).
export const withEditorTheme =
    <P extends object>(Editor: ComponentType<P>) =>
    (props: P) => (
        <ThemeProvider theme={editorTheme}>
            <ZTooltipLookContext.Provider value='editor'>
                {/* I MENU DEGLI EDITOR SONO TENUI (utente, 2026-10-20): la voce scelta in arancio lavato, non pieno. */}
                <DropdownToneContext.Provider value='soft'>
                    <Editor {...props} />
                </DropdownToneContext.Provider>
            </ZTooltipLookContext.Provider>
        </ThemeProvider>
    );

// L'AZIONE PRINCIPALE MORBIDA (prototipo `.btn.tint`, `.btn.primary.rest`): lavato arancio, parola nell'arancio da testo e
// un filo. L'arancio pieno resta agli stati e ai momenti — un interruttore acceso, la conferma —, non a un bottone
// che sta lì tutto il tempo.
export const softAccentButton = css`
    border: 1px solid var(--editor-accent-line, #e2b597);
    background: var(--editor-accent-wash, rgb(232 101 10 / 8%));
    color: var(--editor-accent, #a94700);

    &:hover:not(:disabled),
    &:active:not(:disabled) {
        border-color: var(--editor-accent-line, #e2b597);
        background: var(--editor-accent-wash-hover, rgb(232 101 10 / 16%));
        color: var(--editor-accent, #a94700);
    }
`;

// I CAMPI DI RICERCA DEGLI EDITOR SONO UNO (prototipo V3, `.search-wrap`): 26px, il fondo grigio dei campi, il filo
// delle linee, raggio 8 e la lente piccola e chiara. Si scrive una volta qui, sul contenitore, e vale per ogni
// `SearchInput` dentro gli editor — elenchi, parametri del materiale, menu degli slot — senza toccarli uno per uno.
// Fuori dagli editor e nelle finestre (che stanno in un portale) il campo resta quello del Backoffice. La
// specificità doppia serve a passare sopra al rientro che il campo si dà da sé.
const editorSearchField = css`
    [data-search-field][data-search-field] input {
        height: 26px;
        min-height: 26px;
        padding-left: 28px;
        border: 1px solid var(--editor-field-line, #d1d3d8);
        border-radius: 8px;
        background: var(--editor-field);
        color: var(--editor-fg);
        font-size: 12px;
        box-shadow: none;

        &::placeholder {
            color: var(--editor-faint);
        }

        &:focus {
            border-color: var(--editor-accent);
            outline: none;
            box-shadow: none;
        }
    }

    [data-search-field] [data-search-icon] {
        left: 9px;
        color: var(--editor-faint);

        svg {
            width: 13px;
            height: 13px;
        }
    }
`;

// LE VARIABILI, sul contenitore di ciascun editor. Le `--panel-*` sono i ruoli del pannello delle proprietà (che ha
// anche una veste scura sua): qui diventano gli stessi valori. Le `--composer-*` che copiavano questi grigi non ci sono
// più (utente, 2026-10-23): il Composer usa le `--editor-*`; restano le sue (specie, AI, avvisi, ombra).
export const editorPalette = css`
    --editor-chrome: ${EDITOR_COLORS.chrome};
    --editor-sunken: ${EDITOR_COLORS.sunken};
    --editor-panel: ${EDITOR_COLORS.panel};
    --editor-field: ${EDITOR_COLORS.field};
    --editor-line: ${EDITOR_COLORS.line};
    --editor-field-line: ${EDITOR_COLORS.fieldLine};
    --editor-fg: ${EDITOR_COLORS.fg};
    --editor-muted: ${EDITOR_COLORS.muted};
    --editor-faint: ${EDITOR_COLORS.faint};
    --editor-danger: #c33a31;
    --editor-confirm-bg: #fce4e4;
    --editor-confirm-bg-hover: color-mix(in srgb, var(--editor-danger) 14%, var(--editor-confirm-bg));
    --editor-accent: ${(props) => editorAccent(props.theme).ink};
    --editor-accent-fill: ${(props) => editorAccent(props.theme).fill};
    --editor-accent-brand: ${(props) => editorAccent(props.theme).brand};
    --editor-accent-wash: color-mix(in srgb, var(--editor-accent-brand) 8%, transparent);
    --editor-accent-line: color-mix(in srgb, var(--editor-accent-brand) 38%, ${EDITOR_COLORS.sunken});
    --editor-accent-wash-hover: color-mix(in srgb, var(--editor-accent-brand) 16%, transparent);

    --panel-ground: var(--editor-sunken);
    --panel-surface: var(--editor-panel);
    --panel-line: var(--editor-line);
    --panel-ink: var(--editor-fg);
    --panel-ink-muted: var(--editor-muted);
    --panel-field: var(--editor-field);
    --panel-accent: var(--editor-accent);
    --panel-accent-fill: var(--editor-accent-fill);

    ${confirmingStyle}
    ${editorSearchField}

    /* I CAMPI DI TESTO DEL BACKOFFICE hanno un bordo #DDD (1,3:1): negli editor prendono il filo dei campi, e col fuoco
       l'arancio degli stati. */
    ${CompactInput} {
        border-color: var(--editor-field-line);
    }

    ${CompactInput}:focus {
        border-color: var(--editor-accent);
    }

    /* IL FUOCO DA TASTIERA È ARANCIO, come nel prototipo (utente, 2026-10-20): il contorno di sistema è blu. Vale
       solo dove un componente non ne disegna uno suo (il selettore :where non pesa), e solo da tastiera. */
    /* UN BOTTONE BIANCO AL PASSAGGIO SI RIEMPIE DI GRIGIO (prototipo, .btn:hover; utente 2026-10-21): in testata e nei
       comandi in colonna. Nello Storybook l'Outline accende il filo d'arancio; qui il filo resta grigio e cambia il fondo. */
    ${OutlineButton}:hover:not([disabled]) {
        border-color: var(--editor-line);
        background-color: var(--editor-sunken, #e9ebed);
    }

    ${OutlineButton}:active:not([disabled]) {
        border-color: var(--editor-line);
        background-color: var(--editor-sunken, #e9ebed);
    }

    :where(*:focus-visible) {
        outline: 2px solid var(--editor-accent);
        outline-offset: 1px;
    }

    /* OGNI COMANDO RAGGIUNTO COL TAB SI VEDE (utente, 2026-10-20): bottoni, tab, voci di menu e scelte. Anche dove un
       componente toglieva il contorno o ne disegnava uno blu, qui vince l'arancio. I campi di testo no: lì il fuoco lo
       dice già il bordo. */
    :is(button, a[href], summary, [role='button'], [role='tab'], [role='menuitem'], [role='option'], [role='radio'], [role='treeitem']):focus-visible {
        outline: 2px solid var(--editor-accent) !important;
        outline-offset: 1px;
    }
`;
